import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Nil } from '@pbt/pbt-ui-components'
import { Defaults } from '@pbt/pbt-ui-components'

import { OrderType } from '~/constants/SOAPStates'
import {
  clearSearchOrders,
  fetchMoreForSearch,
  searchOrders,
} from '~/store/actions/orders'
import {
  getAppliedPatientRestrictions,
  getPatientRestrictions,
  type PatientRestrictionType,
} from '~/store/reducers/orders'
import { OrderListType } from '~/types'

export const DEFAULT_INVENTORY_IDS = [
  'PL1J8Oa3', // Drugs
  '3lVqpO7X', // Vet Diet
]

interface UseSwapProductSearch {
  clientId: string | Nil
  onFocus: () => void
  onSearch: (searchAttempt: string) => void
  patientId: string | Nil
  validate: (() => boolean) | undefined
}

export const useSwapProductSearch = ({
  validate,
  patientId,
  clientId,
  onSearch,
  onFocus,
}: UseSwapProductSearch) => {
  const dispatch = useDispatch()

  const appliedPatientRestrictions = useSelector(getAppliedPatientRestrictions)
  const patientRestrictions = useSelector(getPatientRestrictions)

  const [searchTerm, setSearchTerm] = useState('')
  const [searchDisabled, setSearchDisabled] = useState(false)

  const onSearchChange = useCallback(
    (value: string, restrictions = patientRestrictions) => {
      if (!clientId || !patientId) {
        return
      }
      setSearchTerm(value)

      if (value.length < Defaults.MIN_SEARCH_LENGTH) {
        dispatch(clearSearchOrders())
        return
      }

      onSearch(value)

      dispatch(
        searchOrders({
          addAggregations: false,
          patientRestrictions: restrictions,
          listTypes: [OrderListType.SOLD_ON_CHEWY],
          filters: [OrderType.INVENTORY],
          clientId,
          listType: OrderListType.SOLD_ON_CHEWY,
          patientId,
          searchTerm: value,
        }),
      )
    },
    [
      clientId,
      appliedPatientRestrictions,
      patientRestrictions,
      patientId,
      onSearch,
      dispatch,
    ],
  )

  const onSearchMore = useCallback(
    (
      value: string,
      from: number,
      to: number,
      restrictions: PatientRestrictionType[],
    ) => {
      if (!clientId || !patientId) {
        return
      }

      dispatch(
        fetchMoreForSearch({
          addAggregations: false,
          patientRestrictions: restrictions,
          listTypes: [OrderListType.SOLD_ON_CHEWY],
          filters: [OrderType.INVENTORY],
          clientId,
          listType: OrderListType.SOLD_ON_CHEWY,
          patientId,
          searchTerm: value,
          from,
          to,
        }),
      )
    },
    [clientId, appliedPatientRestrictions, patientId, dispatch],
  )

  const onSearchClear = useCallback(() => {
    dispatch(clearSearchOrders())
    setSearchTerm('')
  }, [dispatch])

  const onSearchBlur = useCallback(() => {
    setSearchDisabled(false)
  }, [])
  const onSearchFocus = useCallback(() => {
    onFocus()
    setSearchDisabled(!validate?.())
  }, [])

  return {
    onSearchChange,
    onSearchBlur,
    onSearchFocus,
    searchDisabled,
    searchTerm,
    onSearchMore,
    onSearchClear,
  }
}
